import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, ChevronDown, X, Tag } from "lucide-react";
import { Input } from "./input";
import { RootState } from "../../redux/store";
import { getUserCategories } from "../../redux/Actions/AdminAction";

interface Category {
  id: string;
  name: string;
  subcategories?: string[];
}

interface MultiSelectCategoryDropdownProps {
  selectedCategories: Category[];
  onCategoriesSelect: (categories: Category[]) => void;
  alreadySelectedCategories?: Category[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const MultiSelectCategoryDropdown: React.FC<MultiSelectCategoryDropdownProps> = ({
  selectedCategories,
  onCategoriesSelect,
  alreadySelectedCategories = [],
  placeholder = "Select categories...",
  className = "",
  disabled = false
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { categories, categoriesLoading } = useSelector((state: RootState) => (state as any).admin || {
    categories: { data: [], loading: false },
    categoriesLoading: false
  });

  // Get all already selected category IDs (both in dropdown and saved)
  const allSelectedCategoryIds = new Set([
    ...selectedCategories.map(cat => cat.id),
    ...alreadySelectedCategories.map(cat => cat.id)
  ]);

  const filteredCategories = categories.data.filter((category: Category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !allSelectedCategoryIds.has(category.id)
  );

  // Load categories on component mount
  useEffect(() => {
    if (categories.data.length === 0 && !categoriesLoading) {
      dispatch(getUserCategories({ page: 1, limit: 10, isShowLoader: false }) as any);
    }
  }, [dispatch, categories.data.length, categoriesLoading]);

  // Handle search with debounce
  const handleSearch = useCallback((query: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (query.trim()) {
        dispatch(getUserCategories({ 
          page: 1, 
          limit: 10, 
          isShowLoader: false,
          filters: { search: query }
        }) as any);
      }
    }, 300);

    setSearchTimeout(timeout);
  }, [dispatch, searchTimeout]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleCategoryToggle = (category: Category) => {
    const isSelected = selectedCategories.some(selected => selected.id === category.id);
    
    if (isSelected) {
      // Remove category
      onCategoriesSelect(selectedCategories.filter(selected => selected.id !== category.id));
    } else {
      // Add category
      onCategoriesSelect([...selectedCategories, category]);
    }
  };

  const handleRemoveCategory = (categoryId: string) => {
    onCategoriesSelect(selectedCategories.filter(category => category.id !== categoryId));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px]
          ${disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          }
          transition-colors
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedCategories.length > 0 ? (
            <div className="flex items-center gap-1 flex-wrap">
              <Tag className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm">
                {selectedCategories.length} categor{selectedCategories.length === 1 ? "y" : "ies"} selected
              </span>
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedCategories.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCategoriesSelect([]);
              }}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="pl-10"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {categoriesLoading && categories.data.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-500">Loading categories...</span>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Tag className="h-8 w-8 text-gray-300 mb-2" />
                <p>No categories found</p>
                {searchQuery && (
                  <p className="text-sm text-gray-400">Try a different search term</p>
                )}
              </div>
            ) : (
              filteredCategories.map((category) => {
                const isSelected = selectedCategories.some(selected => selected.id === category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryToggle(category)}
                    className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-4 h-4 border-2 rounded ${
                        isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                      }`}>
                        {isSelected && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{category.name}</p>
                      <p className="text-sm text-gray-500">
                        {category.subcategories?.length || 0} subcategories
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectCategoryDropdown;
